import { el, clear } from "../utils/dom.ts";
import { getEvents, getInsights, type EventItem, type Insight } from "../services/api-client.ts";
import { renderBriefMarkdown } from "../utils/sanitize-markdown.ts";
import { signalSourceLabel } from "./signal-source-label.ts";
import { eventCard } from "./event-card.ts";
import { goToProvince } from "../app/tab-router.ts";

const TOP_EVENTS = 5;
const EVENT_WINDOW_DAYS = 7;

// Yesterday (most recent completed day) in UTC+7 — the newest day a daily-brief
// can exist for. A brief whose `week` (date key) is older than this is stale and
// gets a date label.
function yesterdayUtc7(): string {
  const now = new Date(Date.now() + 7 * 3600_000); // shift into UTC+7
  now.setUTCDate(now.getUTCDate() - 1);
  return now.toISOString().slice(0, 10);
}

// One domain's insight block: daily brief (date-labelled when stale) + the
// day's most severe events, with an optional domain-specific `extra` appended.
// Reuses eventCard (shared XSS-safe renderer) and renderBriefMarkdown (pinned
// DOMPurify) — no parallel copy of the security-sensitive paths.
export class DomainInsightBlock {
  readonly el: HTMLElement;
  private domain: string;
  private title: string;
  private extra?: HTMLElement;
  private briefCollapsed: boolean;

  constructor(opts: { domain: string; title: string; extra?: HTMLElement; briefCollapsed?: boolean }) {
    this.domain = opts.domain;
    this.title = opts.title;
    this.extra = opts.extra;
    // When true, the daily brief is wrapped in a collapsed <details> (metrics-first
    // layout: charts lead, prose is opt-in). Default false = original always-open.
    this.briefCollapsed = opts.briefCollapsed ?? false;
    this.el = el("section", { className: "domain-block" }, ["Đang tải…"]);
  }

  async load(): Promise<void> {
    try {
      const [briefRes, eventsRes] = await Promise.all([
        getInsights(`?type=daily-brief&domain=${this.domain}&limit=1`),
        getEvents(`?domain=${this.domain}&days=${EVENT_WINDOW_DAYS}&limit=20`),
      ]);
      this.render(briefRes.items[0] ?? null, eventsRes.items);
    } catch {
      clear(this.el);
      this.el.append(
        el("div", { className: "domain-block-head" }, [
          el("h2", {}, [this.title]),
          signalSourceLabel("media"),
        ]),
        el("p", { className: "muted" }, ["Không tải được dữ liệu."]),
      );
    }
  }

  private render(brief: Insight | null, events: EventItem[]): void {
    clear(this.el);
    this.el.append(
      el("div", { className: "domain-block-head" }, [
        el("h2", {}, [this.title]),
        signalSourceLabel("media"),
      ]),
    );

    // Daily brief — one fetch (server sorts by date desc); label the date when
    // the newest brief predates yesterday (thin-day fallback made explicit).
    if (brief) {
      const briefBody = el("div", { className: "domain-brief-body" });
      briefBody.innerHTML = renderBriefMarkdown(brief.body_vi); // sanitized
      const staleNote =
        brief.week < yesterdayUtc7()
          ? el("div", { className: "domain-brief-stale" }, [`Bản tin ngày ${brief.week}`])
          : null;
      if (this.briefCollapsed) {
        // Metrics-first: prose folded behind a summary so charts lead. The heading
        // moves into the <summary>; body + stale note live inside the collapsed panel.
        const inner: (Node | string)[] = [briefBody];
        if (staleNote) inner.push(staleNote);
        this.el.append(
          el("details", { className: "domain-brief domain-brief-collapsed" }, [
            el("summary", {}, [`📄 Bản tin: ${brief.title}`]),
            ...inner,
          ]),
        );
      } else {
        const children: (Node | string)[] = [el("h3", {}, [brief.title]), briefBody];
        if (staleNote) children.push(staleNote);
        this.el.append(el("div", { className: "domain-brief" }, children));
      }
    } else {
      this.el.append(
        el("p", { className: "muted domain-empty" }, ["Chưa đủ tín hiệu để tổng hợp bản tin."]),
      );
    }

    // Top events: server orders by date; sort by severity client-side (the API
    // has no severity sort) and take the most severe few.
    const top = [...events].sort((a, b) => b.severity - a.severity).slice(0, TOP_EVENTS);
    if (top.length > 0) {
      const list = el("div", { className: "domain-events" });
      for (const ev of top) {
        list.append(eventCard(ev, { onProvinceClick: (code) => goToProvince(code) }));
      }
      this.el.append(el("h3", { className: "domain-events-title" }, ["Sự kiện nổi bật"]), list);
    }

    if (this.extra) this.el.append(this.extra);
  }
}
